import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";
import Countdown, { CountdownRendererFn } from "react-countdown";
import { OTPInput, SlotProps } from "input-otp";
import { setToken } from "../../graphql/token";
import {
  convertPersianNumbers,
  convertToPersianDigits,
} from "../../utils/convertPersianNumber";
import {
  useCreatePhoneCodeMutation,
  useCreateUserTokenWithPhoneMutation,
} from "../../graphql/generated/graphql";
import LoadingButton from "../Button/LoadingButton";

interface VerifyProps {
  phoneNumber: string;
  onEditPhoneNumber: (newPhoneNumber: string) => void;
  isAlreadySignedUp: boolean;
}

export default function Verify({
  phoneNumber,
  onEditPhoneNumber,
  isAlreadySignedUp,
}: VerifyProps) {
  const [otp, setOtp] = useState<string>("");
  const [isResending, setIsResending] = useState<boolean>(false);
  const [
    createUserTokenWithPhoneMutation,
    { loading: tokenLoading, error: tokenError },
  ] = useCreateUserTokenWithPhoneMutation();

  const [
    createPhoneCodeMutation,
    { loading: resendLoading, error: resendError },
  ] = useCreatePhoneCodeMutation();

  const formik = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .matches(/^\d{7}$/, "کد وارد شده باید ۷ رقمی باشد")
        .required("وارد کردن کد الزامیست"),
    }),
    onSubmit: async (values, actions: FormikHelpers<{ code: string }>) => {
      try {
        const response = await createUserTokenWithPhoneMutation({
          variables: { input: { phoneNumber, code: values.code } },
        });
        const token = response.data?.createUserTokenWithPhone?.userToken?.token;

        if (!token) {
          throw new Error("Temp Token creation failed");
        }

        setToken(token);
        window.location.href = `/company-management?phone=${phoneNumber}`;
      } catch (err: any) {
        console.error("Error during OTP verification:", err);
        actions.setFieldError("code", "خطا در ارتباط با سرور");
      } finally {
        actions.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (otp.length === 7) {
      formik.handleSubmit();
    }
    // eslint-disable-next-line
  }, [otp]);

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      const response = await createPhoneCodeMutation({
        variables: { input: { phoneNumber } },
      });
      if (!response.data?.createPhoneCode?.success) {
        alert(
          response.data?.createPhoneCode?.message || "خطا در ارسال مجدد کد",
        );
      }
    } catch (err) {
      console.error("Error resending code:", err);
    } finally {
      setIsResending(false);
    }
  };

  const Completionist = () => (
    <button
      onClick={handleResendCode}
      disabled={isResending || resendLoading}
      className="flex-1 justify-center border border-primary/40 bg-white text-primary hover:bg-primary/5 rounded-xl px-3 py-2 text-xs font-semibold shadow-sm transition-all"
    >
      {isResending ? "در حال ارسال..." : "ارسال مجدد کد"}
    </button>
  );

  const handleOtpChange = (value: string) => {
    const convertedValue = convertPersianNumbers(value);
    if (/^\d*$/.test(convertedValue)) {
      setOtp(convertedValue);
      formik.setFieldValue("code", convertedValue);
    }
  };

  const renderer: CountdownRendererFn = ({ minutes, seconds, completed }) => {
    if (completed) return <Completionist />;
    const faMinutes = convertToPersianDigits(String(minutes).padStart(2, "0"));
    const faSeconds = convertToPersianDigits(String(seconds).padStart(2, "0"));
    return (
      <div className="flex-1 flex items-center justify-center text-xs bg-gray-50 text-gray-500 border border-gray-200 rounded-xl py-2 px-3 font-medium select-none">
        {`ارسال مجدد کد (${faMinutes}:${faSeconds})`}
      </div>
    );
  };

  const otpInputRef = useRef<any>(null);

  useEffect(() => {
    otpInputRef.current?.focus();
  }, []);

  return (
    <div className="flex flex-col w-full text-right space-y-5">
      {/* Title & Info */}
      <div className="text-center space-y-1.5">
        <h2 className="font-bold text-lg sm:text-xl text-primary">
          تأیید شماره موبایل
        </h2>
        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
          کد ۷ رقمی ارسال شده به شماره{" "}
          <span className="font-bold text-gray-900 dir-ltr inline-block mx-0.5 text-primary">
            {convertToPersianDigits(phoneNumber)}
          </span>{" "}
          را وارد کنید:
        </p>
      </div>

      {/* OTP Input */}
      <div
        className="py-2 w-full flex flex-col items-center justify-center overflow-hidden"
        dir="ltr"
        style={{ direction: "ltr" }}
      >
        <OTPInput
          ref={otpInputRef}
          value={otp}
          onChange={handleOtpChange}
          maxLength={7}
          containerClassName="flex items-center justify-center gap-1.5 sm:gap-2 mx-auto w-full"
          autoFocus={true}
          render={({ slots }) => (
            <div
              className="input-otp flex items-center justify-center gap-1 sm:gap-2"
              dir="ltr"
              style={{ direction: "ltr" }}
            >
              {slots.map((slot, idx) => (
                <Slot key={idx} {...slot} />
              ))}
            </div>
          )}
        />

        {formik.touched.code && formik.errors.code && (
          <span className="text-red-500 text-xs mt-2.5 block text-center font-medium">
            {formik.errors.code}
          </span>
        )}

        {tokenError && (
          <p className="text-red-500 text-xs mt-2.5 text-center font-medium">
            {tokenError.message === "invalid_code_or_phone_number"
              ? "کد وارد شده نامعتبر است"
              : "خطا در تأیید کد"}
          </p>
        )}

        {resendError && (
          <p className="text-red-500 text-xs mt-2.5 text-center font-medium">
            خطا در ارسال مجدد کد
          </p>
        )}
      </div>

      {/* Timer and Edit Actions */}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Countdown date={Date.now() + 119000} renderer={renderer} />
        <button
          onClick={() => onEditPhoneNumber("")}
          className="flex-1 flex items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 rounded-xl px-3 py-2 text-xs font-semibold shadow-sm transition-all"
        >
          ویرایش شماره
        </button>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        onClick={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
        className="w-full flex justify-center items-center rounded-xl bg-primary py-2.5 sm:py-3 px-4 text-sm font-bold text-white shadow-md shadow-primary/20 hover:bg-opacity-90 active:scale-[0.99] transition-all disabled:opacity-60"
        disabled={formik.isSubmitting || tokenLoading}
      >
        {formik.isSubmitting || tokenLoading ? (
          <LoadingButton />
        ) : isAlreadySignedUp ? (
          "ورود"
        ) : (
          "تأیید و ادامه"
        )}
      </button>
    </div>
  );
}

function Slot(props: SlotProps) {
  const displayChar = props.char
    ? convertToPersianDigits(props.char)
    : props.placeholderChar
      ? convertToPersianDigits(props.placeholderChar)
      : "";

  return (
    <div
      className={`relative w-9 sm:w-11 h-12 sm:h-14 text-xl sm:text-2xl font-bold flex items-center justify-center border rounded-xl select-none transition-colors duration-150 ${
        props.isActive
          ? "border-primary ring-2 ring-primary/25 bg-primary/5 text-primary"
          : props.char
            ? "border-gray-300 bg-gray-50/60 text-gray-900"
            : "border-gray-200 bg-white text-gray-400"
      }`}
    >
      {displayChar}
    </div>
  );
}
